<template>
  <div>
    <slot
      :campaign="campaign"
      :status-class="statusClass"
      :days="days"
      :days-left="daysLeft"
      :progress-variant="progressVariant"
    >
      <header>
        <div
          class="logo"
          ref="logo"
          v-if="!isSmall"
          :class="{ 'is-hide': !campaign.image }"
        >
          <!--
            <img :src="'https://hashrunner.com/logo.svg'" :alt="campaign.title" @error="onErrorLogo($event)">
          -->
          <img
            v-if="campaign.image"
            :src="campaign.image"
            :alt="campaign.title"
            @error="onErrorLogo($event)"
          />
        </div>
        <div class="labels">
          <b-badge
            :variant="statusClass(campaign.status)"
            class="text-uppercase"
          >
            {{ $t(`statuses.${campaign.status}`) }}
          </b-badge>
          <b-badge
            v-if="campaign.token"
            variant="danger"
            class="text-uppercase"
          >
            ICO
          </b-badge>
        </div>
        <user-avatar
          v-if="campaign.manager && campaign.manager.name"
          :name="campaign.manager.name"
          :image="campaign.manager.image"
          class="mr-3"
          :show-type="!isSmall"
          :id="campaign.manager.id"
        />
        <div class="right-col d-flex align-items-center">
          <slot name="header-right-col">
            <!-- <watch-list-add :small="isSmall" /> -->
            <router-link
              v-if="
                $store.state.app.role === 'bounty-admin' ||
                  $store.state.app.role === 'bounty-manager'
              "
              :to="{ name: 'campaign-edit', params: { id: campaign.id } }"
              class="btn-link-alt"
            >
              <svg-icon icon="pencil" class="icon-md" />
              <span>{{ $t('editCampaign') }}</span>
            </router-link>
            <!-- <div class="category ml-2">{{ campaign.category.name }}</div> -->
          </slot>
        </div>
      </header>

      <section>
        <h1>
          <router-link
            v-if="hasLink"
            :to="{ name: 'campaign', params: { id: campaign.id } }"
          >
            {{ campaign.title }}
          </router-link>
          <span v-else> {{ campaign.title }} </span>
        </h1>
        <div class="progress-wrapper d-flex flex-wrap">
          <div class="d-flex flex-wrap mr-auto">
            <dates-range
              :start="campaign.start"
              :end="campaign.end"
              class="mr-2"
            />
            <participants :participants="campaign.participants" />
          </div>
          <div class="txt-5-alt text-right text-nowrap">
            <span v-if="campaign.status === 'active'">
              {{ daysLeft }} / {{ days }} {{ $t('daysLeft') }}
            </span>
          </div>
        </div>

        <b-progress
          v-if="campaign.status === 'active'"
          style="width: 100%; height: 0.25rem;"
          class="mt-3 mb-3"
          :value="daysPassed"
          :max="days"
          :variant="progressVariant"
        />

        <slot name="details">
          <b-row no-gutters>
            <b-col v-if="campaign.token" xs="12" md="6" lg="4">
              <div class="stats">
                <span class="stats__title">{{ $t('paymentGuarantee') }}:</span>
                <span class="stats__value" v-if="campaign.paymentGaurantee">
                  {{ campaign.paymentGaurantee.value | formatWideDecimal }}
                  {{ campaign.paymentGaurantee.currency.code }}
                </span>
                <span v-else> No</span>
              </div>
            </b-col>
            <b-col xs="12" md="6" lg="4" v-if="campaign.budget">
              <div class="stats" id="bounty-budget">
                <template>
                  <span class="stats__title">{{ $t('bountyBudget') }}:</span>
                  <span class="stats__value">
                    {{ campaign.budget.amount | formatWideDecimal }}
                    {{
                      campaign.budget.currency && campaign.budget.currency.code
                    }}
                  </span>
                </template>
                <span
                  v-if="typeof campaign.budget === 'number'"
                  style="font-size: .6rem; padding: 0.6em 0.8em; margin-left: 5px;"
                  class="badge badge-success"
                >
                  {{ campaign.budget }} %
                </span>
                <b-tooltip
                  target="bounty-budget"
                  placement="bottom"
                  v-if="campaign.budgetEthereum"
                >
                  <span class="stats__value">
                    {{ campaign.budgetEthereum.amount }}
                    {{ campaign.budgetEthereum.currency.code }}
                  </span>
                </b-tooltip>
              </div>
            </b-col>
            <b-col
              v-if="campaign.token && campaign.token.hardCap"
              xs="12"
              md="6"
              lg="4"
            >
              <div class="stats">
                <span class="stats__title">{{ $t('hardCap') }}:</span>
                <span class="stats__value">
                  {{ campaign.token.hardCap.amount | formatWideDecimal }}
                  {{
                    campaign.token.hardCap.currency &&
                      campaign.token.hardCap.currency.code
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              v-if="campaign.token && campaign.token.softCap"
              xs="12"
              md="6"
              lg="4"
            >
              <div class="stats">
                <span class="stats__title">{{ $t('softCap') }}:</span>
                <span class="stats__value">
                  {{ campaign.token.softCap.amount | formatWideDecimal }}
                  {{
                    campaign.token.softCap.currency &&
                      campaign.token.softCap.currency.code
                  }}
                </span>
              </div>
            </b-col>
            <b-col
              v-if="campaign.token && campaign.token.maxSupply"
              xs="12"
              md="6"
              lg="4"
            >
              <div class="stats">
                <span class="stats__title">{{ $t('maxSupply') }}:</span>
                <span class="stats__value">
                  {{ campaign.token.maxSupply }}
                </span>
              </div>
            </b-col>
            <b-col
              v-if="campaign.coinType && showCoinDesc"
              xs="12"
              md="6"
              lg="4"
            >
              <div class="stats">
                <span class="stats__title">{{ $tc('coinType', 1) }}:</span>
                <span class="stats__value"> {{ campaign.coinType }} </span>
              </div>
            </b-col>
            <b-col v-if="campaign.ticker && showCoinDesc" xs="12" md="6" lg="4">
              <div class="stats">
                <span class="stats__title">{{ $t('ticker') }}:</span>
                <span class="stats__value"> {{ campaign.ticker }} </span>
              </div>
            </b-col>
            <b-col
              v-if="campaign.bountyDistribution && showCoinDesc"
              xs="12"
              md="6"
              lg="4"
            >
              <div class="stats">
                <span class="stats__title">
                  {{ $t('bountyDistribution') }}:
                </span>
                <span class="stats__value">
                  {{ campaign.bountyDistribution }}
                </span>
              </div>
            </b-col>
          </b-row>
        </slot>
        <slot name="tasks" :tasks="campaign.tasks" :campaign="campaign">
          <hr v-if="showTasks && campaign.tasks.length" />
          <list-tasks
            :id="campaign.id"
            v-if="showTasks"
            :list="campaign.tasks"
            class="row"
          />
        </slot>
        <slot name="bottom" :campaign="campaign"></slot>
      </section>
    </slot>
  </div>
</template>

<script>
import WatchListAdd from '@/components/BountyCampaign/WatchListAdd.vue';
import ListTasks from '@/components/BountyCampaign/ListTasks.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import DatesRange from '@/components/DatesRange.vue';
import Participants from '@/components/BountyCampaign/Participants.vue';
import { statusClassesMixin } from '@/utils/mixins.js';

export default {
  mixins: [statusClassesMixin],

  components: {
    WatchListAdd,
    ListTasks,
    UserAvatar,
    DatesRange,
    Participants
  },

  props: {
    campaign: {
      type: Object,
      required: true
    },
    showTasks: {
      type: Boolean,
      default: true
    },
    showCoinDesc: {
      type: Boolean,
      default: true
    },
    hasLink: {
      type: Boolean,
      default: true
    }
  },

  methods: {
    onErrorLogo() {
      this.$refs.logo.classList.add('is-hide');
    }
  },

  computed: {
    progressVariant() {
      if (this.campaign.status === 'active') return 'warning';
      if (this.campaign.status === 'closed') return 'danger';
      return 'info';
    },
    start() {
      return this.$date.format(this.campaign.start, 'DD.MM.YYYY');
    },
    end() {
      return this.$date.format(this.campaign.end, 'DD.MM.YYYY');
    },
    days() {
      return this.$date.differenceInCalendarDays(
        this.campaign.end,
        this.campaign.start
      );
    },
    daysLeft() {
      return Math.abs(
        this.$date.differenceInCalendarDays(this.campaign.end, new Date())
      );
    },
    daysPassed() {
      return Math.abs(
        this.$date.differenceInCalendarDays(new Date(), this.campaign.start)
      );
    },
    isSmall() {
      return (
        this.$root.screen.lg ||
        this.$root.screen.md ||
        this.$root.screen.sm ||
        this.$root.screen.xs
      );
    }
  }
};
</script>

<style lang="scss" scoped>
header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
}

h1 {
  a {
    color: $body-color;
    @extend %base-transition;

    &:hover {
      text-decoration: none;
      color: $link-color;
    }
  }
}

h2 {
  margin-bottom: 1.5rem;
}

.logo {
  max-width: 160px;
  width: 160px;
  margin-right: 2rem;

  img {
    display: inline-block;
  }

  &.is-hide {
    display: none;
  }
}

.labels {
  display: flex;
  align-items: center;
  margin-right: 1rem;

  span {
    display: block;
    text-transform: uppercase;
  }
}

.right-col {
  margin-left: auto;
  margin-right: 1rem;

  &:last-child {
    margin-right: 0;
  }
}

.category {
  text-transform: uppercase;
  font-size: 12px;
  color: $primary;
  font-weight: $font-weight-bold;
}

.progress-days {
  margin-left: auto;
}

.progress-wrapper {
  font-size: 14px;
}

.stats {
  font-size: 14px;
  margin-right: 1rem;

  & > * {
    vertical-align: middle;
  }

  &:last-child {
    margin-right: 0;
  }

  &__title {
    color: $light;
  }

  &__value {
    text-transform: uppercase;
  }
}

.bounty-campaign {
  &--mini {
    margin-bottom: 10px;

    .logo {
      max-width: 100px;
      margin-right: 1rem;
    }
  }
}
</style>
